import cookie from '@s-ui/js/lib/cookie'
import {getDecision} from '@s-ui/pde'
import {getContentfulDocumentPlainText} from '@adv-mt/mt-document-contentful'
import {
  getImagesUrlsAdapted,
  imageUrlAdapterAspectRatios,
  imageUrlAdapterImageTypes,
  imageUrlAdapterTransformTypes
} from '@adv-mt/mt-hook-use-image-url-adapter'

import {getInitialBreadcrumbsProps} from './getInitialBreadcrumbsProps.js'
import {getInitialCommentsProps} from './getInitialCommentsProps.js'
import {getInitialRelatedAdsProps} from './getInitialRelatedAdsProps.js'
import {getImageSizeFromUrl} from './utils.js'

const MAX_NUM_OF_ADS = 4
const DEFAULT_INITIAL_PROPS = {
  relatedContent: [],
  images: ['']
}

const cleanUpInitialPath = ({domainConfig, path}) => {
  const {NEWS_COMMENTS_BASE_PATH} = domainConfig.get('page')
  return path.replace(NEWS_COMMENTS_BASE_PATH, '')
}

export const getInitialProps = async ({routeInfo, context: {cookies, domain, i18n, pde}}) => {
  const {location: {pathname: path, search: queryString, state} = {}, params = {}} = routeInfo
  const {culture, locale} = i18n
  const domainConfig = domain.get('config')
  const {DEFAULT_SEARCH} = domainConfig.get('search')
  const {PATHS} = domainConfig.get('COMMON')
  const {UNSELECTED_OFFER_TYPE} = domainConfig.get('searchFilters')
  const {articleId} = params
  const {NEWS_COMMENTS_PAGE_NAME, NEWS_HOME_PATH_PATTERN} = domainConfig.get('page')

  const {decide} = getDecision(pde)
  const {enabled: isNewsBookmarksEnabled} = decide('ff_web_growth_news_bookmarks', {queryString})
  const normalizedPath = cleanUpInitialPath({domainConfig, path})
  const _getDetailsInfo = () =>
    domain.get('get_news_detail_use_case').execute({articleId, slug: normalizedPath, locale})
  const _getPageName = () => domain.get('get_page_name_page_use_case').execute({path})

  const [
    {
      value: [newsDetailDomainError, newsDetailDomainResponse]
    },
    {
      value: {pageName}
    }
  ] = await Promise.allSettled([_getDetailsInfo(), _getPageName()])

  if (newsDetailDomainError) {
    return {
      ...DEFAULT_INITIAL_PROPS,
      __HTTP__: {redirectTo: NEWS_HOME_PATH_PATTERN}
    }
  }

  const isNewsCommentsPage = pageName === NEWS_COMMENTS_PAGE_NAME

  const {
    allowComments,
    author,
    body,
    category,
    customContentConfig,
    date,
    id,
    images,
    prosAndCons,
    publicationDate,
    relatedContent,
    seoMetaInformation,
    tagclouds,
    tag,
    title,
    totalNumberOfComments,
    updateDate,
    valoration,
    vehicles = [],
    video
  } = newsDetailDomainResponse

  const [{make = {}, model = {}} = {}] = vehicles
  const hasMakeModel = Boolean(make?.name && model?.name)

  const search = {
    ...DEFAULT_SEARCH,
    offerTypeId: 1,
    makeId: make?.id,
    model: {id: model?.id}
  }
  const _getModelDetailAds = () => domain.get('get_model_detail_relevant_ads_use_case').execute(search)

  const _getBreadcrumbs = () =>
    getInitialBreadcrumbsProps({
      domain,
      i18n,
      pageName,
      newsDetailDomainResponse
    })

  const _getComments = () =>
    getInitialCommentsProps({
      domain,
      newsDetailDomainResponse
    })
  const _getSeoTagclouds = () =>
    domain.get('get_tagcloud_seo_use_case').execute({
      pageName
    })
  const _getRating = () => domain.get('get_news_rating_reviews_use_case').execute({articleId: id})

  const _getRelatedAds = async () => getInitialRelatedAdsProps({i18n, domain, vehicles})
  const [firstVehicle] = vehicles.length
    ? vehicles.map(({make, model}) => ({make: make.name, makeId: make.id, model}))
    : []

  const {ALL_VEHICLES} = domainConfig.get('COMMON').LISTING_SECTION_IDS

  const _getProfessionalSellerAds = async () => {
    const REQUEST_ITEMS_SIZE = 8

    if (!vehicles || !vehicles.length) return [{}, null]

    return domain.get('get_search_results_ads_use_case').execute({
      ...DEFAULT_SEARCH,
      listingSectionId: ALL_VEHICLES,
      sellerTypeId: domainConfig.get('dealer').SELLER_TYPE_PROFESSIONAL,
      offerTypeId: UNSELECTED_OFFER_TYPE,
      page: domainConfig.get('DEFAULT_PAGE_NUMBER'),
      size: REQUEST_ITEMS_SIZE,
      vehicles: [firstVehicle]
    })
  }

  const _getProfessionalSellerAdsListingUrl = async () => {
    return domain.get('get_url_params_search_use_case').execute({
      listingSectionId: ALL_VEHICLES,
      search: {
        ...DEFAULT_SEARCH,
        sellerTypeId: domainConfig.get('dealer').SELLER_TYPE_PROFESSIONAL,
        offerTypeId: UNSELECTED_OFFER_TYPE,
        page: domainConfig.get('DEFAULT_PAGE_NUMBER'),
        vehicles: [firstVehicle]
      }
    })
  }

  const _getIsMakeConfigurableVehicle = async () => {
    return domain.get('get_is_vehicle_configurable_vehicles_use_case').execute({makeId: make.id})
  }

  const _getFaqsForUrlSearch = () => domain.get('get_faqs_for_url_search_use_case').execute({targetUrl: normalizedPath})

  const _getSavedNewsIds = () =>
    isNewsBookmarksEnabled ? domain.get('get_saved_news_ids_use_case').execute() : Promise.resolve([null, []])

  const [
    {value: breadcrumbsResponse},
    {value: commentsResponse},
    {value: extendedTagClouds = []},
    {
      value: [articleRatingError, articleRatingResponse]
    },
    {value: relatedAdsResponse},
    {
      value: [getModelDetailAdsUseCaseError, getModelDetailAdsUseCaseResult]
    },
    {
      value: [getProfessionalSellerAdsError, getProfessionalSellerAdsResponse]
    },
    {
      value: [getProfessionalSellerAdsListingUrlError, getProfessionalSellerAdsListingUrlResponse]
    },
    {
      value: [getIsMakeConfigurableVehicleError, getIsMakeMakeConfigurableVehicleResponse]
    },
    {
      value: [faqsForUrlError, faqsForUrlResponse]
    },
    {
      value: [savedNewsIdsError, savedNewsIdsResponse]
    }
  ] = await Promise.allSettled([
    _getBreadcrumbs(),
    _getComments(),
    _getSeoTagclouds(),
    _getRating(),
    _getRelatedAds(),
    _getModelDetailAds(),
    _getProfessionalSellerAds(),
    _getProfessionalSellerAdsListingUrl(),
    hasMakeModel ? _getIsMakeConfigurableVehicle() : Promise.resolve([null, null]),
    _getFaqsForUrlSearch(),
    _getSavedNewsIds()
  ])

  const articlePlainText = getContentfulDocumentPlainText(body)

  const faqsForUrlItems =
    !faqsForUrlError && faqsForUrlResponse && faqsForUrlResponse.toJSON()?.faqs?.items.length > 0
      ? faqsForUrlResponse.toJSON().faqs.items
      : []

  const savedArticleIdsArray = !savedNewsIdsError && savedNewsIdsResponse ? savedNewsIdsResponse : []
  const isCurrentArticleSaved = savedArticleIdsArray.includes(id)

  const {HAS_SEEN_SAVE_NEWS_COACHMARK} = domainConfig.get('COMMON').COOKIES
  const hasSeenSaveNewsCoachmark = Boolean(
    typeof window !== 'undefined'
      ? cookie.get(HAS_SEEN_SAVE_NEWS_COACHMARK)
      : cookie.parse(cookies)[HAS_SEEN_SAVE_NEWS_COACHMARK]
  )

  const _getSeoData = ({faqs, images, rating, valoration}) => {
    const {ARTICLE_BODY_IMAGE_WIDTH} = domainConfig.get('editorial')
    const [firstImageUrl] = images
    const mappedImages = getImagesUrlsAdapted({
      images: [{src: firstImageUrl}],
      options: {
        aspectRatio: imageUrlAdapterAspectRatios.WIDE_SCREEN,
        imageType: imageUrlAdapterImageTypes.ARTICLE_MAIN_IMAGE_META,
        imageWidth: ARTICLE_BODY_IMAGE_WIDTH,
        transformType: imageUrlAdapterTransformTypes.DEVICE_CROP
      }
    })
    const firstImageUrlMapped = mappedImages[0].src

    const getImageSizes = () => {
      const {height: firstImageHeight, width: firstImageWidth} = getImageSizeFromUrl({url: firstImageUrlMapped})

      return {
        firstImageHeight,
        firstImageWidth,
        url: firstImageUrlMapped
      }
    }

    const getImageInfo = () => {
      return firstImageUrlMapped && getImageSizes()
    }

    const getImageUrls = () => {
      if (!firstImageUrl || !firstImageUrlMapped) return {}

      const makeUrl = aspectRatio =>
        getImagesUrlsAdapted({
          images: [{src: String(firstImageUrl)}],
          options: {
            aspectRatio,
            imageType: imageUrlAdapterImageTypes.ARTICLE_MAIN_IMAGE_META,
            imageWidth: ARTICLE_BODY_IMAGE_WIDTH,
            transformType: imageUrlAdapterTransformTypes.DEVICE_CROP
          }
        })[0]?.src

      const makeUrlWithType = (aspectRatio, imageType) =>
        getImagesUrlsAdapted({
          images: [{src: String(firstImageUrl)}],
          options: {
            aspectRatio,
            imageType,
            imageWidth: ARTICLE_BODY_IMAGE_WIDTH,
            transformType: imageUrlAdapterTransformTypes.DEVICE_CROP
          }
        })[0]?.src

      return {
        url16x9: makeUrl(imageUrlAdapterAspectRatios.WIDE_SCREEN),
        url4x3: makeUrlWithType(
          imageUrlAdapterAspectRatios.FULL_SCREEN,
          imageUrlAdapterImageTypes.ARTICLE_MAIN_IMAGE_META_43
        ),
        url1x1: makeUrlWithType(1, imageUrlAdapterImageTypes.ARTICLE_MAIN_IMAGE_META_11)
      }
    }

    return domain.get('get_seo_data_use_case').execute({
      culture,
      pageName,
      params: {
        ...seoMetaInformation,
        articlePlainText,
        author,
        breadcrumb: breadcrumbsResponse,
        category,
        faqs,
        image: getImageInfo(),
        imageUrls: getImageUrls(),
        path,
        publicationDate,
        rating,
        updateDate,
        valoration,
        vehicles,
        video
      }
    })
  }

  const [seoDataError, seoDataResponse = {}] = await _getSeoData({
    faqs: faqsForUrlItems,
    images,
    rating: articleRatingResponse,
    valoration
  })

  if (seoDataError) console.error(seoDataError)
  if (articleRatingError) console.error(articleRatingError)
  if (getModelDetailAdsUseCaseError) console.error(getModelDetailAdsUseCaseError)

  let modelDetailAdsNew
  let modelDetailAdsRenting
  let bestOffers
  const showConfigurator =
    hasMakeModel && !getIsMakeConfigurableVehicleError && getIsMakeMakeConfigurableVehicleResponse?.isConfigurable
  if (!getModelDetailAdsUseCaseError) {
    const {newVehicles, rentingVehicles} = getModelDetailAdsUseCaseResult
    modelDetailAdsNew = newVehicles.items?.slice(0, MAX_NUM_OF_ADS)
    modelDetailAdsRenting = rentingVehicles.items?.slice(0, MAX_NUM_OF_ADS)
    const highlightedNewVehicleAd = newVehicles?.highlightedAd
    const highlightedRentingVehicleAd = rentingVehicles?.highlightedAd
    bestOffers = [showConfigurator ? null : highlightedNewVehicleAd, highlightedRentingVehicleAd]
      .filter(Boolean)
      .map(ad => {
        return {...ad, make, model}
      })
  }

  const {scripts: structuredDataScripts = []} = seoDataResponse.head

  const getStructuredData = ({scripts}) => scripts.map(script => JSON.parse(script.innerHTML))

  const structuredDataJsonList = getStructuredData({
    scripts: structuredDataScripts
  })

  const helmet = {
    ...seoDataResponse,
    structuredDataScripts: structuredDataJsonList
  }
  const mergeTagclouds = ({i18n, extendedTagClouds, tagclouds}) => {
    const updatedTagClouds = tagclouds.map(tagcloudsGroup => {
      const {tags} = tagcloudsGroup
      const translatedTags = tags.map(({link, ...rest}) => ({
        ...rest,
        link: i18n.t(link)
      }))
      return {...tagcloudsGroup, tags: translatedTags}
    })
    return [...updatedTagClouds, ...extendedTagClouds]
  }

  const {items: professionalSellerAds} =
    !getProfessionalSellerAdsError && getProfessionalSellerAdsResponse ? getProfessionalSellerAdsResponse : {items: []}

  return {
    adsNew: modelDetailAdsNew,
    adsRenting: modelDetailAdsRenting,
    allowComments,
    articleId: id,
    hasSeenSaveNewsCoachmark,
    isCurrentArticleSaved,
    isNewsBookmarksEnabled,
    articleSlug: normalizedPath,
    author,
    bestOffers,
    body,
    breadcrumbs: breadcrumbsResponse,
    category,
    comments: commentsResponse,
    customContentConfig,
    date,
    faqsForUrlItems,
    helmet,
    images,
    isNewsCommentsPage,
    make,
    model,
    origin: state?.origin,
    pageName,
    professionalSellerAds,
    professionalSellerAdsListingUrl:
      !getProfessionalSellerAdsListingUrlError && getProfessionalSellerAdsListingUrlResponse
        ? getProfessionalSellerAdsListingUrlResponse.url
        : PATHS.ADS_LIST_SEARCH,
    prosAndCons,
    rating: articleRatingResponse,
    relatedAds: relatedAdsResponse,
    relatedContent,
    savedArticleIds: savedArticleIdsArray,
    showConfigurator,
    tagClouds: mergeTagclouds({i18n, extendedTagClouds, tagclouds}),
    tag,
    title,
    totalNumberOfComments,
    vehicles
  }
}
